import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  useMemo,
} from "react";
import CheEnquiry from "./CheEnquiry";
import CheReceivedOrder from "./CheReceivedOrder";
import CheConfirmOrder from "./CheConfirmOrder";
import ChePackedOrder from "./ChePackedOrder";
import CheCancelledOrder from "./CheCancelledOrder";
import CheShipmentRecevied from "./CheShipmentRecevied";
import apiService from "../../../Services/authenticationServices";
import { ChevronDown, Search } from "lucide-react";
import { PageHeader, Tabs } from "../../ui/caliper";

const FILTER_INPUT =
  "h-8 px-2.5 border border-hairline rounded-cb-sm bg-panel text-t-md text-fg-default placeholder:text-fg-subtle outline-none focus:border-ink-500 focus:shadow-cb-focus transition-shadow";

const CheOrdersContext = createContext();

/* Filter state -> the query the tabs send: warehouses joined, blanks dropped. */
const toApiFilter = (filter) => {
  const apiFilter = {
    ...filter,
    circleCode: filter.circleCode && filter.circleCode.length > 0 ? filter.circleCode.join(",") : "",
  };
  Object.keys(apiFilter).forEach((key) => {
    if (apiFilter[key] === "") {
      delete apiFilter[key];
    }
  });
  return apiFilter;
};

/* How long typing in a filter waits before the list reloads. Search applies at once. */
const FILTER_DEBOUNCE_MS = 400;

export const useCheOrders = () => useContext(CheOrdersContext);

const CheOrders = () => {
  const user = JSON.parse(localStorage.getItem("loginObj"));
  const circleCode = localStorage.getItem("circleCode");
  const [activeTab, setActiveTab] = useState("enquiry");
  const [clinic, setClinic] = useState([]);
  const [filteredClinics, setFilteredClinics] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const warehouseDropdownRef = useRef(null);
  const eclinicDropdownRef = useRef(null);
  const childRef = useRef();
  const dropdownRef = useRef(null);
  const [defaultWHList, setdefaultWHList] = useState([]);

  const [warehouseList, setwarehouseList] = useState([]);

  const [loggedInUser] = useState(
    user.userRolesList.find(
      (user) => user.roleCode === "PHHEAD" || user.roleCode === "PH"
    )
  );
  const [selectedSource, setselectedSource] = useState("CHE");

  const stateList = [
    { name: 'Odisha', value: 'OD' },
    { name: 'Chhattisgarh', value: 'CG' },
    { name: 'Jharkhand', value: 'JH' }
  ];

  const [filter, setFilter] = useState({
    state:
      loggedInUser?.roleCode !== "PHHEAD"
        ? localStorage.getItem("userState")
        : "OD",
    circleCode: circleCode != null ? [circleCode] : [],
    enquiryId: "",
    orderId: "",
    mobileNo: "",
    fromDate: "",
    toDate: "",
    eclinicCode: "",
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        warehouseDropdownRef.current &&
        !warehouseDropdownRef.current.contains(event.target)
      ) {
        if (dropdownOpen === "warehouse") {
          setDropdownOpen(null);
        }
      }
      if (
        eclinicDropdownRef.current &&
        !eclinicDropdownRef.current.contains(event.target)
      ) {
        if (dropdownOpen === "eclinic") {
          setDropdownOpen(null);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  useEffect(() => {
    if (searchInput) {
      const filtered = clinic.filter(
        (item) =>
          item.name.toLowerCase().includes(searchInput.toLowerCase()) ||
          item.code.toLowerCase().includes(searchInput.toLowerCase())
      );
      setFilteredClinics(filtered);
    } else {
      setFilteredClinics(clinic);
    }
  }, [searchInput, clinic]);

  const setFilterField = (field, value) => {
    setFilter((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetFilterField = (field) => {
    if (field === "circleCode") {
      setFilter((prev) => ({
        ...prev,
        [field]: [],
      }));
    } else {
      setFilter((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
    if (field === "eclinicCode") {
      setSearchInput("");
    }
  };

  /* The filters the tabs actually load with. Typing updates `filter` at once (so the
     inputs stay responsive) and this copy after a short pause, so a 13-digit order
     ID is one request rather than thirteen. */
  const [appliedFilter, setAppliedFilter] = useState(filter);
  useEffect(() => {
    const t = setTimeout(() => setAppliedFilter(filter), FILTER_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [filter]);
  /* Rebuilt only when a filter value changes. A new object on every render made
     each tab reload on any re-render of this page (opening a dropdown, typing in
     the eClinic search box). */
  const appliedKey = JSON.stringify(appliedFilter);
  const apiFilter = useMemo(() => toApiFilter(JSON.parse(appliedKey)), [appliedKey]);

  const resetAllFilters = () => {
    const next = {
      state:
        loggedInUser?.roleCode !== "PHHEAD"
          ? localStorage.getItem("userState")
          : filter?.state
            ? filter?.state
            : "OD",
      circleCode: circleCode != null ? [circleCode] : [],
      enquiryId: "",
      orderId: "",
      mobileNo: "",
      fromDate: "",
      toDate: "",
      eclinicCode: "",
    };
    setFilter(next);
    /* A reset applies at once — no pause, no load with the old filters. */
    setAppliedFilter(next);
    setSearchInput("");
  };

  const tabs = [
    { id: "enquiry", label: "Enquiries" },
    { id: "received", label: "Received Orders" },
    { id: "confirm", label: "Confirm Orders" },
    // { id: "shipment", label: "Shipment Received" },
    { id: "packed", label: "Packed Orders" },
    { id: "cancelled", label: "Cancelled Orders" },
  ];

  useEffect(() => {
    resetAllFilters();
  }, [activeTab]);

  const renderTabContent = () => {
    switch (activeTab) {
      case "enquiry":
        return <CheEnquiry ref={childRef} />;
      case "received":
        return <CheReceivedOrder ref={childRef} />;
      case "confirm":
        return <CheConfirmOrder ref={childRef} />;
      case "shipment":
        return <CheShipmentRecevied ref={childRef} />;
      case "packed":
        return <ChePackedOrder ref={childRef} />;
      case "cancelled":
        return <CheCancelledOrder ref={childRef} />;
      default:
        return null;
    }
  };

  const handleFilterChange = (field, value) => {
    setFilter((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleReset = (field) => {
    if (field === "circleCode") {
      setFilter((prev) => ({
        ...prev,
        [field]: [],
      }));
    } else {
      setFilter((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
    if (field === "eclinicCode") {
      setSearchInput("");
    }
  };

  const fetchData = async () => {
    try {
      let payload = {
        type: "C",
        status: 1,
      };
      const response = await apiService.getEclinics(payload);
      const newEnquiries = response.data.successList || [];
      setClinic(newEnquiries);
      setFilteredClinics(newEnquiries);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchData();
    wareHouseData();
  }, []);

  const handleSearch = () => {
    /* Filters typed but not yet applied: apply them now — the tab reloads when its
       filter changes. Otherwise reload the current filters. */
    if (JSON.stringify(filter) !== appliedKey) {
      setAppliedFilter(filter);
      return;
    }
    if (childRef.current?.onSearch) {
      childRef.current.onSearch();
    }
  };

  const wareHouseData = async () => {
    try {
      const response = await apiService.getwareHouseDetails();
      const res = response.data.successList;
      console.log(res)
      setdefaultWHList(res)

      setwarehouseList(res.filter((e) => e.stateCode == filter.state));
    } catch (err) {
      console.error("Error fetching order details:", err);
    }
  };
  useEffect(() => {
    setwarehouseList(defaultWHList.filter(e => e.stateCode == filter.state));
  }, [filter.state]);
  return (
    <div className="flex flex-col gap-4 cb-fade-in">
      <PageHeader
        title="CHE / Engage orders"
        sub="Community Health Engagement — orders received via eClinics"
      />

      <div className="flex">
        <div className="flex flex-wrap items-center gap-2 w-full bg-panel border border-hairline rounded-cb-md px-3 py-2.5">
          {loggedInUser?.roleCode === "PHHEAD" && (
            <>
              <select
                value={filter.state}
                onChange={(e) => handleFilterChange("state", e.target.value)}
                className={`${FILTER_INPUT} w-full sm:w-44`}
              >
                <option value="">Select State</option>
                {stateList.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.name}
                  </option>
                ))}
              </select>

              <div className="relative w-full sm:w-60" ref={warehouseDropdownRef}>
                <div className="flex items-center gap-2">
                  <div className="relative w-full">
                    <div
                      className={`${FILTER_INPUT} flex items-center justify-between cursor-pointer`}
                      onClick={() =>
                        setDropdownOpen(
                          dropdownOpen === "warehouse" ? null : "warehouse"
                        )
                      }
                    >
                      <span className="truncate">
                        {filter.circleCode.length > 0
                          ? `${filter.circleCode.length} warehouse(s) selected`
                          : "Select warehouses"}
                      </span>
                      <ChevronDown
                        size={14}
                        strokeWidth={1.9}
                        className={`ml-2 text-fg-muted transition-transform ${dropdownOpen === "warehouse" ? "rotate-180" : ""}`}
                      />
                    </div>

                    {dropdownOpen === "warehouse" && (
                      <div className="absolute z-50 w-full mt-1 bg-panel border border-hairline rounded-cb-md shadow-cb-md max-h-60 overflow-y-auto cb-scroll py-1">
                        {warehouseList.map((warehouse) => {
                          const checked = filter.circleCode.includes(
                            warehouse.circleCode
                          );
                          return (
                            <label
                              key={warehouse.pharmacyCode}
                              className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-surface-hover text-t-sm"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  let updated = [...filter.circleCode];
                                  if (e.target.checked) {
                                    updated.push(warehouse.circleCode);
                                  } else {
                                    updated = updated.filter(
                                      (c) => c !== warehouse.circleCode
                                    );
                                  }
                                  handleFilterChange("circleCode", updated);
                                }}
                                className="accent-ink-600"
                              />
                              {warehouse.pharmacyName}
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {filter.circleCode.length > 0 && (
                    <button
                      onClick={() => handleReset("circleCode")}
                      className="text-red-700 text-t-xs font-semibold hover:underline whitespace-nowrap"
                    >
                      Reset
                    </button>
                  )}
                </div>
              </div>
            </>
          )}

          {selectedSource !== "B2C" && activeTab !== "shipment" && (
            <input
              type="text"
              placeholder="Enquiry ID"
              value={filter.enquiryId}
              onChange={(e) => handleFilterChange("enquiryId", e.target.value)}
              className={`${FILTER_INPUT} w-full sm:w-44`}
            />
          )}

          {(selectedSource === "CHE" && activeTab !== "enquiry") ||
            selectedSource === "B2C" ? (
            <input
              type="text"
              placeholder="Order ID"
              value={filter.orderId}
              onChange={(e) => handleFilterChange("orderId", e.target.value)}
              className={`${FILTER_INPUT} w-full sm:w-44`}
            />
          ) : null}

          <div className={`${FILTER_INPUT} flex items-center w-full sm:w-52 relative`}>
            <input
              type="text"
              onFocus={(e) => {
                e.target.type = "date";
                e.target.showPicker?.();
              }}
              onBlur={(e) => {
                if (!e.target.value) {
                  e.target.type = "text";
                }
              }}
              value={filter.fromDate || ""}
              onChange={(e) => {
                if (e.target.type === "date") {
                  handleFilterChange("fromDate", e.target.value);
                }
              }}
              className="flex-1 min-w-0 outline-none bg-transparent text-fg-default placeholder:text-fg-subtle"
              placeholder={filter.toDate ? "" : "From date"}
              max={filter.toDate || undefined}
            />
            {filter.fromDate && (
              <button
                onClick={() => handleReset("fromDate")}
                className="text-red-700 text-t-xs font-semibold hover:underline ml-2"
              >
                Reset
              </button>
            )}
          </div>

          <div className={`${FILTER_INPUT} flex items-center w-full sm:w-52 relative`}>
            <input
              type={filter.toDate ? "date" : "text"}
              value={filter.toDate || ""}
              onChange={(e) => handleFilterChange("toDate", e.target.value)}
              onFocus={(e) => {
                if (!filter.toDate) {
                  e.target.type = "date";
                }
              }}
              onBlur={(e) => {
                if (!e.target.value) {
                  e.target.type = "text";
                }
              }}
              className="flex-1 min-w-0 outline-none bg-transparent text-fg-default placeholder:text-fg-subtle"
              placeholder={filter.toDate ? "" : "To date"}
              min={filter.fromDate}
            />
            {filter.toDate && (
              <button
                onClick={() => handleReset("toDate")}
                className="text-red-700 text-t-xs font-semibold hover:underline ml-2"
              >
                Reset
              </button>
            )}
          </div>

          {selectedSource === "CHE" && (
            <div className="relative w-full sm:w-64" ref={eclinicDropdownRef}>
              <div className="flex items-center gap-2">
                <div className="relative w-full">
                  <div
                    className={`${FILTER_INPUT} flex items-center justify-between cursor-pointer`}
                    onClick={() =>
                      setDropdownOpen(
                        dropdownOpen === "eclinic" ? null : "eclinic"
                      )
                    }
                  >
                    <span className="truncate">
                      {filter.eclinicCode
                        ? clinic.find((c) => c.code === filter.eclinicCode)
                          ?.name || "Select eClinic"
                        : "Select eClinic"}
                    </span>
                    <ChevronDown
                      size={14}
                      strokeWidth={1.9}
                      className={`ml-2 text-fg-muted transition-transform ${dropdownOpen === "eclinic" ? "rotate-180" : ""}`}
                    />
                  </div>

                  {dropdownOpen === "eclinic" && (
                    <div className="absolute z-50 w-full mt-1 bg-panel border border-hairline rounded-cb-md shadow-cb-md max-h-60 overflow-y-auto cb-scroll">
                      <div className="p-2 border-b border-hairline-subtle">
                        <input
                          type="text"
                          placeholder="Search eClinic…"
                          className="w-full px-2 py-1.5 border border-hairline rounded-cb-sm text-t-sm outline-none focus:border-ink-500"
                          value={searchInput}
                          onChange={(e) => setSearchInput(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <div className="py-1">
                        {filteredClinics.length > 0 ? (
                          filteredClinics.map((center) => (
                            <div
                              key={center.code}
                              className={`px-3 py-1.5 text-t-sm cursor-pointer hover:bg-surface-hover ${filter.eclinicCode === center.code
                                ? "bg-surface-selected text-ink-700 font-semibold"
                                : ""
                                }`}
                              onClick={() => {
                                handleFilterChange("eclinicCode", center.code);
                                setDropdownOpen(null);
                              }}
                            >
                              {center.name}
                            </div>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-fg-muted text-t-sm">
                            No eClinics found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {filter.eclinicCode && (
                  <button
                    onClick={() => {
                      handleReset("eclinicCode");
                      setSearchInput("");
                    }}
                    className="text-red-700 text-t-xs font-semibold hover:underline whitespace-nowrap"
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>
          )}

          <button
            onClick={handleSearch}
            className="ml-auto inline-flex items-center gap-1.5 h-8 px-4 bg-ink-600 text-fg-on-ink text-t-md font-medium rounded-cb-sm hover:bg-ink-700 active:bg-ink-800 transition-colors"
          >
            <Search size={14} strokeWidth={1.9} />
            Search
          </button>
        </div>
      </div>

      <Tabs
        tabs={tabs}
        value={activeTab}
        onChange={setActiveTab}
        className="-mt-1"
      />

      <CheOrdersContext.Provider
        value={{
          filter: apiFilter,
          setFilterField,
          resetFilterField,
          resetAllFilters,
          originalFilter: filter,
        }}
      >
        <div>{renderTabContent()}</div>
      </CheOrdersContext.Provider>
    </div>
  );
};

export default CheOrders;